import { Builder, defaultMainColors } from './builder.js';
import {
	apparelSourceColors,
	ColorTriple,
	ColorTuple,
	isCommandBasedGraphics,
	MainColorGroup,
	mainColorGroups,
	rgbToHex,
} from '../../../common/data/colors.js';
import { Component, ComponentChild, Fragment } from 'preact';
import { Icon } from '../icons.js';
import {
	apply,
	capitalize,
	ensure,
	findMatch,
	isIn,
	isNone,
	keepExisting,
	maybe,
	objectNotEmpty,
} from '../../../common/util.js';
import { ColorPicker, ColorPickerInput } from '../colorpicker.js';
import { ColorPreset } from '../preset.js';
import { CountryPicker } from '../country.js';
import { Input } from '../input.js';
import { Maybe } from '../../../common/data.js';
import { classNames, getEventValue } from '../../lib/util.js';
import { getUploadPreviewURL } from '../../lib/images.js';
import { BuilderSettingsBlock } from './settings.js';
import { getDesignRosterFields } from '../../../common/data/roster.js';
import { DesignPart, fontInchSizes, movablePartTypes, textPartTypes } from '../../../common/data/designs.js';
import { PublicFlow } from '../../../common/data/flows.js';
import { PublicDomain } from '../../../common/data/domains.js';
import { getSiteContext } from '../../templates/site.js';

export function getColorsForPart(
	part: DesignPart,
	flow: PublicFlow,
	presentColors: MainColorGroup[],
): Maybe<{
		id: string;
		value: ColorTuple;
	}[]> {
	const colors = part.colors;
	if (!colors) return;
	if (part.productTemplate) return presentColors
		.filter(cid => (
			Object.values(part.productTemplate!.colors || {}).find(clist => clist.includes(cid))
			&& !part.productTemplate?.color_lock?.[cid]
		))
		.map(cid => ({
			id: cid,
			value: colors[cid] || part.productTemplate?.template_info?.colors?.[cid] || defaultMainColors[cid]!,
		}));
	if (part.type == 'apparel') return presentColors.map(cid => ({
		id: cid,
		value: colors[cid] || defaultMainColors[cid]!,
	}));
	if (isIn(part.type, textPartTypes)) return ['fill', 'stroke'].map(cid => ({
		id: cid,
		value: colors[cid]!,
	}));
	if (part.type == 'flag') return [{
		id: 'outline',
		value: colors.outline || [0, 0, 0],
	}];
	if (part.type == 'pw-logo') return [{
		id: 'logo',
		value: colors.logo || [0, 0, 0],
	}];
	if (isCommandBasedGraphics(flow) && part.vector_upload?.commands_palette) return part.vector_upload.commands_palette.map((rgb, cidx) => ({
		id: String(cidx + 1),
		value: apply(colors[cidx + 1], c => c ?? rgb.slice(0, 3) as ColorTriple),
	}));
}

interface PartConfigProps {
	part: DesignPart;
	builder: Builder;
	removable: boolean;
	mobile?: boolean;
	domain: PublicDomain;
}

interface PartConfigState {
	activeColor?: Maybe<string>;
}

interface DesktopPartProps extends PartConfigProps {
	open: boolean;
	toggle: () => void;
}

const pwLogoPositions: Record<string, string> = {
	'pw-bottom-left': 'Bottom left',
	'pw-bottom-right': 'Bottom right',
	'pw-top-left': 'Top left',
	'pw-top-right': 'Top right',
	'pw-front': 'Front top center',
	'pw-left-sleeve': 'Left sleeve',
	'pw-right-sleeve': 'Right sleeve',
	'pw-left-chest': 'Left chest',
	'pw-right-chest': 'Right chest',
};

export function getPartTitle(part: DesignPart) {
	if (part.type == 'flag') return 'Flag';
	if (part.type == 'name') return 'Player Name';
	if (part.type == 'number') return 'Player Number';
	if (part.type == 'text') return part.text!.text || 'Text';
	if (part.type == 'alias') return 'Alias';
	if (part.type == 'pw-logo') return `${getSiteContext()?.domain.value.brand_name ?? 'Brand'} logo`;
	if (part.upload) return part.upload.filename;
	if (part.type == 'apparel') return (
		maybe(part.productTemplate?.sanmar_color_name, cn => `Color: ${cn}`)
		?? 'Color'
	);
	return '';
}

function ColorCircles({ part, builder }: { part: DesignPart; builder: Builder }) {

	const design = part.productTemplate;
	const designs = builder.props.productTemplates;
	const cc = 'builder-settings__color-circle';

	return <div className="builder-settings__color-circles">
		{designs.map(des => {
			const type = des.type == 'commands' || objectNotEmpty(des.color_lock) ? 'commands' : des.type;
			const colors = keepExisting([
				...des.type != 'commands' && des.color ? [des.color] : [],
				...maybe(des.color_lock, Object.values) ?? [],
			]);
			return <div onClick={() => builder.manager.setProductTemplate(des)}
				class={`${cc} ${cc}--${type} ${cc}${des.id === design?.id ? '--current' : ''}`}
				style={
					type == 'texture' ? { backgroundImage: `url(/data/product-designs/${des.texture})` }
					: type == 'solid' ? { background: rgbToHex(des.color!) }
					: {}
				}
			>
				{colors.map(v => <div style={{ backgroundColor: rgbToHex(v) }} />)}
			</div>;
		})}
	</div>;

}

const icons: Record<string, () => ComponentChild> = {
	logo: () => <Icon name="image-file"/>,
	apparel: () => <Icon name="color-palette" />,
	flag: () => <Icon name="flag" />,
	name: () => <Icon name="user" />,
	number: () => <Icon name="one" />,
	text: () => <Icon name="text" />,
	alias: () => <Icon name="alias" />,
};

export class DesktopPart extends Component<DesktopPartProps, {}> {

	render({ part, builder, open, toggle, removable, domain }: DesktopPartProps) {

		const { product, flow } = builder.props;
		const colors = getColorsForPart(part, flow, builder.props.presentColors);

		const title = getPartTitle(part);

		return <BuilderSettingsBlock
			title={title}
			icon={icons[part.type]?.() || (part.type == 'pw-logo' && (part.domain == 'ts' ? <Icon name="t-s" /> : <Icon name="e-x" />)) || null}
			open={open}
			toggle={toggle}
			previews={colors ? () => colors.map((color, i) => <div
				className={`builder-settings__color-preview builder-settings__color-preview--small ${color.value ? '' : 'builder-settings__color-preview--transparent'}`}
				style={{ backgroundColor: maybe(color.value, rgbToHex) }}
			/>) : null}
			body={() => <PartConfig builder={builder} part={part} removable={removable} domain={domain} />}
		/>;

	}

}

export class PartConfig extends Component<PartConfigProps, PartConfigState> {

	componentDidMount() {
		const { builder, part } = this.props;
		const { product, flow } = builder.props;
		if (['name', 'alias', 'number'].includes(part.type) && this.props.builder.manager.state.roster[0]?.[part.type as ('name' | 'alias' | 'number')]) {
			const colors = getColorsForPart(part, flow, builder.props.presentColors);
			this.setState( { activeColor: colors?.find(color => color.value.every(v => v === 255))?.id } );
		}
	}

	render({ part, builder, removable, mobile }: PartConfigProps, { activeColor }: PartConfigState) {
		const { product, flow } = builder.props;
		const { features } = product;
		const colors = getColorsForPart(part, flow, builder.props.presentColors);
		let color = activeColor && colors && findMatch(colors, { id: activeColor }) || null;
		if (part.type == 'flag' && part.flag?.outline) color = colors![0]!;
		const rosterFields = getDesignRosterFields(builder.props.product, builder.manager.state.parts);

		const needCommands = isCommandBasedGraphics(flow);
		const haveCommands = needCommands && !!part.vector_upload?.commands;
		const colorList = builder.props.threadColors || null;
		const usedThreads = part.threads && new Set(keepExisting(Object.values(part.threads)));

		const isColorTemplateApparel = part.type === 'apparel' && builder.props.product.features?.templatesAreColors;
		
		let presets = builder.state.colorPresets;
		if (presets && builder.props.isAdmin) presets = [...presets, apparelSourceColors];
		
		if (part.type == 'logo' && needCommands && !haveCommands) return <div class="builder-settings__wait">
			<h3>Preprocessing logo</h3>
			<p>Preparing the uploaded file to allow further customization. This can take up to a minute.</p>
			<button
				className="builder-settings__remove-part"
				onClick={() => builder.manager.removePart(part.id)}
			>
				<Icon name="close"/>
				remove
			</button>
		</div>;

		const flagCheckbox = (side: 'left' | 'right', flag: DesignPart['flag']) => <label>
			<input
				type="checkbox"
				checked={part.flag![side] !== 'off'}
				onChange={e => builder.manager.updatePart(part.id, { flag: { ...part.flag, [side]: (e.target as HTMLInputElement).checked ? 'on' : 'off' } })}
			/>
			<span>{capitalize(side)}</span>
		</label>;

		return <div>

			{part.type == 'flag' && <div class="builder-settings__switches">
				{rosterFields.flag == 'present' ? <CountryPicker
					flag={true}
					value={part.flag!.country}
					handler={country => builder.manager.updatePart(part.id, { flag: { ...part.flag, country: country && country.id } })}
				/> : <div>Choose country within roster</div>}
			</div>}

			{colors && presets && part.type == 'apparel' && features?.popularColors && !mobile && <ColorPresets
				presets={presets}
				builder={builder}
				onSelect={colors => builder.manager.setPreset(colors)}
			/>}

			{colors && part.type != 'flag' && !isColorTemplateApparel && !colorList && <div className="builder-settings__colors">
				{colors && colors.map(color => <div
					data-color-id={color.id}
					className={classNames('builder-settings__color-preview', color.id === activeColor && '--active')}
					style={{ backgroundColor: rgbToHex(color.value) }}
					onMouseDown={e => this.startColorDrag(e, { part, builder }, e.target as HTMLElement, color.id)}
					onTouchStart={e => this.startColorDrag(e, { part, builder }, e.target as HTMLElement, color.id)}
				/>)}

				{mobile && part.type === 'apparel' && activeColor !== undefined && <div class="builder-settings__color-pie" onClick={() => this.setState({ activeColor: undefined })}><Icon name="color-pie" /></div>}

				{part.type == 'pw-logo' && <div className="builder-settings__switches">
					<select onChange={e => builder.manager.updatePart(part.id, { position: (e.target as HTMLSelectElement).value })}>
						{Object.entries(features?.points || {}).filter(([k, v]) => k.indexOf('pw-') == 0 && v).map(
							([k]) => <option value={k} selected={part.position == k}>{pwLogoPositions[k] || k}</option>
						)}
					</select>
				</div>}

			</div>}

			{isColorTemplateApparel && <ColorCircles part={part} builder={builder} />}

			{colorList && !isColorTemplateApparel && colors && <Fragment>
				<div class="builder-settings__embroidery-colors">
					<div class="builder-settings__embroidery-colors-list">
						<div class="builder-settings__embroidery-colors-list-label">Colors in Logo:</div>
						<div class="builder-settings__embroidery-colors-list-grid">
							{colors.map(color => <div
								className={`builder-settings__color-preview ${color.id === activeColor ? 'builder-settings__color-preview--active' : ''} ${color.value ? '' : 'builder-settings__color-preview--transparent'}`}
								style={{ backgroundColor: maybe(color.value, rgbToHex) }}
								onClick={() => this.setState({ activeColor: activeColor == color.id ? null : color.id })}
							/>)}
						</div>
					</div>
					<div class={`builder-settings__embroidery-colors-pick builder-settings__embroidery-colors-pick--${color ? 'active' : 'inactive'}`}>
						<div class="builder-settings__embroidery-colors-pick-label">
							{flow.decoration == 'embroidery' ? 'Thread colors:' : 'Available colors:'}
						</div>
						<div class="builder-settings__embroidery-colors-pick-grid">
							{colorList.filter(c => isNone(c.fallback)).map(c => <div
								className={`builder-settings__color-preview builder-settings__color-preview--small builder-settings__color-preview--${color && c.id === part.threads?.[color.id] ? 'active' : usedThreads?.has(c.id) ? 'used' : 'unused'}`}
								style={{ backgroundColor: rgbToHex(c.rgb) }}
								onClick={() => builder.manager.setPartColor(part.id, color!.id, c.rgb, c.id)}
							/>)}
							<div
								className={`builder-settings__color-preview builder-settings__color-preview--small builder-settings__color-preview--transparent builder-settings__color-preview--${color && null === part.threads?.[color.id] ? 'active' : 'inactive'}`}
								onClick={() => builder.manager.setPartColor(part.id, color!.id, null, null)}
							/>
						</div>
					</div>
				</div>
				{flow.decoration == 'embroidery' && <div class="builder-settings__color-count-note">
					<p>
						<strong>{usedThreads?.size}</strong> distinct thread colors selected.
					</p>
					<p>
						Six thread colors are included in the price, additional ones are +$2 each, up to 15 total.
					</p>
				</div>}
				{flow.decoration == 'screen-print' && <div class="builder-settings__color-count-note">
					<p>
						<strong>{usedThreads?.size}</strong> distinct colors selected.
					</p>
				</div>}
			</Fragment>}

			{part.text && <div className="builder-settings__switches">
				<select
					onChange={e => builder.manager.updatePart(part.id, { text: { ...part.text!, font: (e.target as HTMLSelectElement).value } })}
					className="builder-settings__font-picker"
				>
					<option value="">None</option>
					{builder.props.fonts.map(font => <option value={font.id} selected={part.text!.font == font.name || part.text!.font == font.id}>{font.name}</option>)}
				</select>
				{product.features?.physicalScale && <select
					onChange={e => builder.manager.updatePart(
						part.id,
						{ text: { ...part.text!, sizeIn: apply(getEventValue(e), v => v ? Number(v) : null) } },
					)}
					value={part.text.sizeIn ?? ''}
				>
					<option value="">Fit Font</option>
					{fontInchSizes.map(n => <option value={n}>{n}"</option>)}
				</select>}
				{!colorList && <div>
					<label>
						<input
							type="checkbox"
							checked={part.text.fill}
							onChange={e => builder.manager.updatePart(part.id, { text: { ...part.text!, fill: (e.target as HTMLInputElement).checked } })}
						/>
						<span>Fill</span>
					</label>
					<label>
						<input
							type="checkbox"
							checked={part.text.stroke}
							onChange={e => builder.manager.updatePart(part.id, { text: { ...part.text!, stroke: (e.target as HTMLInputElement).checked } })}
						/>
						<span>Outline</span>
					</label>
				</div>}
				<select
					onChange={e => builder.manager.updatePart(part.id, { text: { ...part.text!, rotate: parseInt((e.target as HTMLSelectElement).value) || 0 } })}
				>
					{[0, 90, 180, 270].map(deg => <option value={deg} selected={part.text!.rotate == deg}>{deg}°</option>)}
				</select>
			</div>}

			{part.type == 'flag' && <div class="builder-settings__switches">
				{flagCheckbox('left', part.flag)}
				{flagCheckbox('right', part.flag)}
				<label>
					<input
						type="checkbox"
						checked={part.flag!.outline}
						onChange={e => builder.manager.updatePart(part.id, { flag: { ...part.flag!, outline: (e.target as HTMLInputElement).checked } })}
					/>
					<span>Outline</span>
				</label>
			</div>}

			{color && !colorList && <ColorPicker
				color={color.id}
				label={color.id[0]!.toUpperCase() + color.id.slice(1)}
				value={color.value}
				onChange={value => builder.manager.setPartColor(part.id, color.id, value)}
				startDrag={cb => builder.startDrag(cb)}
				profile={builder.props.flow.color_profile || 'rgb'}
				componentRanges={builder.props.flow.color_ranges}
				admin={builder.props.isAdmin}
			/>}

			{!color && presets && part.type == 'apparel' && features?.popularColors && mobile && <ColorPresets
				presets={presets}
				builder={builder}
				onSelect={colors => builder.manager.setPreset(colors)}
			/>}

			{part.type == 'text' && <div className="builder-settings__switches">
				<Input
					value={part.text!.text}
					attributes={{ maxLength: 25 }}
					handler={value => builder.manager.updatePart(part.id, { text: { ...part.text!, text: value.trim() } })}
				/>
			</div>}
			{['name', 'number', 'alias'].includes(part.type) && <div className="builder-settings__switches">
				{/* FIXME */}
			</div>}

			{part.type == 'logo' && part.upload && !colorList && <div class="builder-settings__logo">
				<div class="builder-settings__logo-preview">
					<img src={getUploadPreviewURL(part.upload, builder.props.flow.color_profile || 'rgb')} />
				</div>
				{part.upload.colors && <div class="builder-settings__logo-colors">
					{part.upload.colors.map(c => <ColorPickerInput value={c} profile={builder.props.flow.color_profile || 'rgb'} />)}
				</div>}
			</div>}

			{isIn(part.type, movablePartTypes) && <form className="builder-settings__switches">
				{[['front', 'Front'], ['back', 'Back']].map(([sid, sname]) => <label>
					<input
						name="side"
						type="radio"
						value={sid}
						checked={part.point?.side == sid}
						onChange={e => builder.manager.updatePart(part.id, { point: { ...part.point, side: sid }, position: 'custom' })}
					/>
					<span>{sname}</span>
				</label>)}
				{Object.entries(features?.areas ?? {}).length > 1 && <select onChange={e => builder.manager.updatePart(part.id, { point: { ...part.point, area: getEventValue(e) || undefined }, position: part.type })}>
					{Object.entries(features?.areas ?? {}).map(([aid, area]) => <option value={aid} selected={part.point?.area == aid}>{aid}</option>)}
				</select>}
				<label>
					<input
						type="checkbox"
						checked={part.position == 'custom'}
						onChange={e => builder.manager.updatePart(part.id, {
							position: (e.target as HTMLInputElement).checked ? 'custom' : part.type,
						})}
					/>
					<span>Custom position</span>
				</label>
			</form>}

			{removable && <button
				className="builder-settings__remove-part"
				onClick={() => builder.manager.removePart(part.id)}
			>
				<Icon name="close"/>
				remove
			</button>}
			{(part.type == 'text' || part.type == 'logo') && [
				<button
					className="builder-settings__move-part"
					onClick={() => builder.manager.movePart(part.id, -1)}
				>
					<Icon name="arrow-up" />
					up
				</button>,
				<button
					className="builder-settings__move-part"
					onClick={() => builder.manager.movePart(part.id, 1)}
				>
					<Icon name="arrow-down" />
					down
				</button>,
			]}
			{part.type == 'logo' && <div>
				<select onChange={e => builder.manager.updatePart(part.id, { drawOverColor: (getEventValue(e) || undefined) as MainColorGroup })}>
					<option value="">draw over</option>
					{mainColorGroups.map(cid => <option value={cid} selected={part.drawOverColor == cid}>{cid}</option>)}
				</select>
				<label>
					<input
						type="checkbox"
						checked={part.wrap !== false}
						onChange={e => builder.manager.updatePart(part.id, { wrap: (e.target as HTMLInputElement).checked })}
					/>
					<span>Wrap</span>
				</label>
				<label>
					<input
						type="checkbox"
						checked={part.cropToArea !== false}
						onChange={e => builder.manager.updatePart(part.id, { cropToArea: (e.target as HTMLInputElement).checked })}
					/>
					<span>Crop to area</span>
				</label>
				<select onChange={e => builder.manager.updatePart(part.id, { tile: getEventValue(e) || undefined })}>
					<option value="">no tile</option>
					{['horizontal', 'vertical', 'both'].map(cid => <option value={cid} selected={part.tile == cid}>{cid}</option>)}
				</select>
			</div>}

		</div>;

	}

	startColorDrag(e: Event, { builder, part }: { builder: Builder; part: DesignPart }, el: HTMLElement, cid: string) {

		const { product, flow } = builder.props;

		e.preventDefault();
		const pid = part.id;
		const initActiveColor = this.state.activeColor;
		this.setState({ activeColor: cid });

		const getColor = (id: string) => {
			const p = ensure(builder.manager.getPart(pid));
			return p.colors?.[id] ?? ensure(findMatch(ensure(getColorsForPart(p, flow, builder.props.presentColors)), { id })).value;
		};
		const value = getColor(cid);

		let cur = cid;
		let swapped = false;
		builder.startDrag((cx, cy) => {
			const over = (document.elementFromPoint(cx, cy) as HTMLElement).dataset.colorId;
			if (!over || over == cur) return;
			const swap = getColor(over);
			builder.manager.setPartColor(pid, over, value);
			builder.manager.setPartColor(pid, cur, swap);
			if (this.state.activeColor == cur) this.setState({ activeColor: over });
			cur = over;
			swapped = true;
		}, () => {
			this.setState({ activeColor: initActiveColor == cur && !swapped ? null : cur });
		});

	}
}

export function ColorPresets({ presets, builder, onSelect }: { presets: ColorTuple[][]; builder: Builder; onSelect: (colors: ColorTuple[]) => void }) {

	let shown = presets;

	const colors = builder.state.logoColors || [];
	const f = builder.state.featuredColor;
	const s = builder.state.secondaryColor;
	if (f) {
		const b = [0, 0, 0];
		const w = [255, 255, 255];
		const g1 = [47, 51, 54];
		const g2 = [105, 106, 109];
		const g3 = [163, 160, 148];
		const g4 = [152, 161, 152];

		shown = [
			[b, b, f, w, f],
			[b, b, f, f, w],
			[b, b, w, f, f],
			[f, f, b, w, b],
			[b, f, w, f, b],
			[w, f, b, b, f],
			[f, f, w, w, w],
			[w, w, f, f, w],
			[b, g1, f, f, g1],
			[b, f, g2, g2, f],
			[b, g3, f, g3, f],
			[f, g4, b, f, w],
			[f, g2, f, g2, g2],
			[b, f, s, s, w],
			[s, f, s, w, w],
			[w, f, s, s, f],
			[f, f, s, f, s],
			[b, f, s, f, s],
		]
			.filter(p => !p.includes(undefined))
			.map(t => [...t, ...t.slice(1, 4), ...t.slice(1, 4), t[0]]) as ColorTuple[][];

	}

	return <div class="builder-settings__color-presets">
		{f && [
			<div class="builder-settings__color-presets-featured-label">
				Featured color:
			</div>,
			<div class="builder-settings__color-presets-featured">
				{colors.map((color, i) => <div
					className={`builder-settings__color-presets-featured-color ${color.join() == f.join() ? 'builder-settings__color-presets-featured-color--active' : ''}`}
					style={{ backgroundColor: rgbToHex(color) }}
					onClick={() => builder.setState({
						featuredColor: color,
						secondaryColor: colors[i + 1] || colors.find(c => c.join() != color.join()),
					})}
				/>)}
			</div>,
		]}
		<div class="builder-settings__color-presets-list">
			{shown.map(colors => <ColorPreset
				key={JSON.stringify(colors)}
				colors={colors}
				groups={builder.props.presentColors}
				onClick={() => onSelect(colors)}
			/>)}
		</div>
	</div>;

}
